import React, { ChangeEvent, DragEvent, useEffect, useState } from "react";
import { Tooltip } from "primereact/tooltip";

import { fileIcon, videoFileIcon } from "assets/icons";
import { Spinner } from "components/Spinner";
import { logger } from "utils/logger";

import { fileUploadConfigService, FileUploadConfig } from "../../services/fileUploadConfigService";
import { ErrorMessage } from "../Modals/CreateModal/CreateModal.styles";
import { FileDisplay } from "../FileToDisplay/FileToDisplay.types";
import {
  DownloadableArea,
  DropZone,
  DropZoneCaption,
  DropZoneContent,
  DropZoneTitle,
  FileIcon,
  FileItem,
  FileList,
  FileName,
  InfoText,
  ProgressPercent,
  RemoveFileButton,
  SpinnerIconContainer,
} from "./DragAndDropField.styles";
import { DragAndDropFieldProps } from "./DragAndDropField.types";

const DragAndDropField: React.FC<DragAndDropFieldProps> = ({
  accept = "",
  placeholder = "Нажмите или перетащите файл(ы) для загрузки",
  infoText = "",
  files: externalFiles,
  files_from_storage,
  onFileSelected,
  onRemoveFile,
  showFileList = true,
  dropZoneBackground,
  maxFiles,
  maxFileSize,
  disabled = false,
  variant = "default",
}: DragAndDropFieldProps) => {
  const [files, setFiles] = useState<File[]>(externalFiles || []);
  const [storageFiles, setStorageFiles] = useState<FileDisplay[]>(files_from_storage || []);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<FileUploadConfig | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const fileConfig = await fileUploadConfigService.getConfig();
        setConfig(fileConfig);
      } catch (loadError) {
        logger.error("Failed to load file upload config:", loadError);
        setError("Не удалось загрузить настройки загрузки файлов");
      }
    };
    loadConfig();
  }, []);

  const effectiveMaxFiles = maxFiles || config?.max_files_count || 10;
  const effectiveMaxFileSize = maxFileSize || config?.max_file_size || 10 * 1024 * 1024;

  useEffect(() => {
    setFiles(externalFiles || []);
  }, [externalFiles]);

  useEffect(() => {
    setStorageFiles((files_from_storage || []).map((file) => ({ ...file })));
  }, [files_from_storage]);

  const addFiles = (newFiles: File[]) => {
    if (disabled) return;

    const oversizeFiles = newFiles.filter((file) => file.size > effectiveMaxFileSize);
    if (oversizeFiles.length > 0) {
      setError(`Превышен размер файла ${Math.floor(effectiveMaxFileSize / (1024 * 1024))} МБ`);
    }

    const validFiles = newFiles.filter((file) => file.size <= effectiveMaxFileSize);
    const combined = [...files, ...validFiles];

    if (effectiveMaxFiles && combined.length > effectiveMaxFiles) {
      setError(`Можно загрузить не более ${effectiveMaxFiles} файлов`);
      const allowed = combined.slice(0, effectiveMaxFiles);
      setFiles(allowed);
      onFileSelected(allowed);
      return;
    }

    const uniqueFiles = combined.filter(
      (file, idx, self) => self.findIndex((candidate) => candidate.name === file.name) === idx,
    );

    setFiles(uniqueFiles);
    onFileSelected(uniqueFiles);

    if (oversizeFiles.length === 0 && uniqueFiles.length <= effectiveMaxFiles) {
      setError(null);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (disabled) return;
    addFiles(Array.from(event.dataTransfer.files) as File[]);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? (Array.from(event.target.files) as File[]) : [];
    addFiles(selectedFiles);
  };

  const removeFile = (fileToRemove: File | FileDisplay) => {
    if (disabled) return;

    if (fileToRemove instanceof File) {
      const updatedFiles = files.filter((file) => file !== fileToRemove);
      setFiles(updatedFiles);
      onFileSelected(updatedFiles);
      return;
    }

    const updatedStorageFiles = storageFiles.filter((file) => file.id !== fileToRemove.id);
    setStorageFiles(updatedStorageFiles);
    onRemoveFile?.(fileToRemove.id);
  };

  const downloadFile = (url: string, filename?: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || url.split("/").pop() || "download";
    link.target = "_blank";
    link.click();
  };

  return (
    <div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <DropZone
        $dragOver={dragOver}
        $variant={variant}
        $disabled={disabled}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{ background: dropZoneBackground }}
      >
        <input type="file" accept={accept} multiple onChange={handleFileChange} disabled={disabled} />
        {variant === "compact" ? (
          <DropZoneContent>
            <DropZoneTitle>{placeholder}</DropZoneTitle>
            {infoText ? <DropZoneCaption>{infoText}</DropZoneCaption> : null}
          </DropZoneContent>
        ) : (
          <p>{placeholder}</p>
        )}
      </DropZone>
      {infoText && variant !== "compact" ? <InfoText>{infoText}</InfoText> : null}
      {showFileList && (
        <FileList>
          {[...storageFiles]
            .sort((a, b) => {
              const aProcessing = a.type === "video" && a.status === "processing";
              const bProcessing = b.type === "video" && b.status === "processing";
              if (aProcessing !== bProcessing) return aProcessing ? 1 : -1;
              return a.id - b.id;
            })
            .map((file: FileDisplay, index: number) => {
              const isProcessing = file.type === "video" && file.status === "processing";
              const progressPercent =
                file.progress_percent !== undefined && file.progress_percent > 0
                  ? file.progress_percent
                  : null;

              const iconElement = isProcessing ? (
                <SpinnerIconContainer>
                  <Spinner $size="2rem" />
                  {progressPercent !== null && <ProgressPercent>{progressPercent}%</ProgressPercent>}
                </SpinnerIconContainer>
              ) : (
                <FileIcon as={file.type === "video" ? videoFileIcon : fileIcon} aria-hidden="true" />
              );

              const progressText = isProcessing
                ? ` ${progressPercent !== null ? `${progressPercent}%` : "0%"}`
                : file.name;

              return (
                <FileItem key={`storage-${index}`} style={{ opacity: isProcessing ? 0.6 : 1 }}>
                  {!isProcessing ? (
                    <DownloadableArea onClick={() => downloadFile(file.url, file.name)}>
                      {iconElement}
                    </DownloadableArea>
                  ) : (
                    iconElement
                  )}
                  <FileName className={`file-link-${index}`}>{progressText}</FileName>
                  <RemoveFileButton
                    type="button"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      removeFile(file);
                    }}
                  >
                    ×
                  </RemoveFileButton>
                  <Tooltip
                    target={`.file-link-${index}`}
                    content={file.name}
                    mouseTrack
                    mouseTrackLeft={10}
                  />
                </FileItem>
              );
            })}
          {files.map((file: File, index: number) => (
            <FileItem key={`file-${index}`}>
              <FileIcon as={fileIcon} aria-hidden="true" />
              <FileName className={`file-link-${index}`}>{file.name}</FileName>
              <RemoveFileButton type="button" onClick={() => removeFile(file)}>×</RemoveFileButton>
              <Tooltip
                target={`.file-link-${index}`}
                content={file.name}
                mouseTrack
                mouseTrackLeft={10}
              />
            </FileItem>
          ))}
        </FileList>
      )}
    </div>
  );
};

export default DragAndDropField;
